// ScheduleGrid — template-driven on-screen schedule table + template editor.
// Schedules rebuild Phase 3 (docs/SCHEDULES_REBUILD_SPEC.md §5).
//
// The grid IS the template: columns render at template widths, resize by
// dragging header dividers, reorder/rename/re-block through the column
// menu, and add/remove columns. **Templates are a live single source
// (Cimon, 2026-07-07): edits AUTOSAVE as you go** — the same debounce
// pattern as billing-summary drafts — because the structure IS the
// template and the schedule is a living document. The only copies in the
// system are published issues (document_sends). A deliberate fork exists
// as "New template from this layout…"; multi-user editing is last-write-
// wins, accepted. Field selection is audience-gated: internal-only fields
// (pricing) never appear in the picker on client/gc templates — the same
// gate buildSchedulePayloadPure enforces at render time, and an edit that
// would violate it is held locally (red note) instead of autosaved.
//
// Editing CELLS (two-way item writes, overrides, custom values) is Phase 4;
// this grid renders read-only data cells and stamps data-item-id +
// data-field on each cell so Phase 4 wires editing without restructuring.
//
// Design language: hairline borders + ink text on white; GROUND_OFF is the
// only fill (room bands — sanctioned low-attention secondary surface);
// three-tier button geometry; eyebrow labels 10px/0.07em.

import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  SCHEDULE_FIELDS,
  scheduleRowBagsForTemplate,
  groupScheduleRowsByRoom,
  groupScheduleRowsByInstallEvent,
  resolveScheduleCell,
  listScheduleFieldsForAudience,
  scheduleFieldAllowedForAudience,
  canEditScheduleField,
} from '../utils';
import {
  INK_1, INK_2, INK_3, INK_4, LINE_ROW, LINE_CONTAINER, GROUND_OFF,
  ZONE_FUCHSIA, ZONE_GREEN, ALERT_RED_TEXT,
} from '../lib/designTokens';

const h = React.createElement;

// Relative template width unit → on-screen pixels. The PDF renderer scales
// the same relative units to the page, so screen and print proportions match.
export const UNIT_PX = 7;
const MIN_UNITS = 5;

// ── Pure column-edit helpers (exported for tests) ────────────────────────────

export function resizeColumn(columns, colId, newWidthUnits) {
  const w = Math.max(MIN_UNITS, Math.round(newWidthUnits));
  return (columns || []).map(c => c.id === colId ? Object.assign({}, c, { width: w }) : c);
}

export function moveColumn(columns, colId, dir) {
  const cols = (columns || []).slice();
  const i = cols.findIndex(c => c.id === colId);
  const j = i + (dir < 0 ? -1 : 1);
  if (i < 0 || j < 0 || j >= cols.length) return cols;
  const tmp = cols[i]; cols[i] = cols[j]; cols[j] = tmp;
  return cols;
}

export function removeColumn(columns, colId) {
  return (columns || []).filter(c => c.id !== colId);
}

let _colSeq = 0;
function freshColId(prefix) {
  _colSeq += 1;
  return prefix + '_' + Date.now().toString(36) + '_' + _colSeq;
}

export function newFieldColumn(fieldKey) {
  const meta = SCHEDULE_FIELDS[fieldKey] || {};
  return {
    id: freshColId(fieldKey),
    label: meta.label || fieldKey.toUpperCase(),
    width: 20,
    blocks: [{ kind: 'field', path: fieldKey }],
  };
}

export function newCustomColumn(label) {
  return { id: freshColId('custom'), label: label || 'NOTES (SCHEDULE)', width: 20, kind: 'custom' };
}

// Strip any internal-only field from a template headed to a client/gc
// audience — defense in depth behind the picker (same rule the payload
// builder throws on).
export function templateAudienceViolations(template) {
  const bad = [];
  ((template && template.columns) || []).forEach(c => {
    (c.blocks || []).forEach(b => {
      if (b && b.kind === 'field' && !scheduleFieldAllowedForAudience(b.path, template.audience)) {
        bad.push({ column: c.label || c.id, path: b.path });
      }
    });
  });
  return bad;
}

// ── Small styled primitives ──────────────────────────────────────────────────

const EYEBROW = { fontSize: 10, letterSpacing: '0.07em', textTransform: 'uppercase', color: INK_3, fontWeight: 500 };
const INPUT = { padding: '5px 8px', border: '1px solid ' + LINE_CONTAINER, borderRadius: 4, fontSize: 12, color: INK_1, boxSizing: 'border-box' };

function MiniBtn(props) {
  return h('button', {
    onClick: props.onClick,
    disabled: props.disabled,
    title: props.title || '',
    style: Object.assign({
      padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: props.disabled ? 'default' : 'pointer',
      background: '#fff', color: props.danger ? ALERT_RED_TEXT : INK_2,
      border: '0.5px solid ' + (props.danger ? ALERT_RED_TEXT : LINE_CONTAINER),
      opacity: props.disabled ? 0.4 : 1,
    }, props.style || {}),
  }, props.children);
}

// ── Column menu (rename / width / move / blocks / remove) ────────────────────

function BlockRow({ block, audience, onChange, onRemove }) {
  const allowed = listScheduleFieldsForAudience(audience).filter(k => k !== 'image');
  if (block.kind === 'text') {
    return h('div', { style: { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 5 } },
      h('span', { style: { fontSize: 10, color: INK_3, minWidth: 34 } }, 'text'),
      h('input', {
        style: Object.assign({}, INPUT, { flex: 1 }), value: block.value || '',
        placeholder: 'Fixed text…',
        onChange: e => onChange(Object.assign({}, block, { value: e.target.value })),
      }),
      h(MiniBtn, { onClick: onRemove, danger: true, title: 'Remove line' }, '×')
    );
  }
  return h('div', { style: { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 5, flexWrap: 'wrap' } },
    h('span', { style: { fontSize: 10, color: INK_3, minWidth: 34 } }, 'field'),
    h('select', {
      style: Object.assign({}, INPUT, { flex: 2, minWidth: 130 }),
      value: block.path || '',
      onChange: e => onChange(Object.assign({}, block, { path: e.target.value })),
    },
      block.path && !allowed.includes(block.path) ? h('option', { value: block.path }, block.path + ' (not allowed)') : null,
      allowed.map(k => h('option', { key: k, value: k },
        (SCHEDULE_FIELDS[k].label || k) + '  ·  ' + k))
    ),
    h('input', {
      style: Object.assign({}, INPUT, { width: 64 }), value: block.prefix || '', placeholder: 'prefix',
      onChange: e => onChange(Object.assign({}, block, { prefix: e.target.value || undefined })),
    }),
    h('input', {
      style: Object.assign({}, INPUT, { width: 64 }), value: block.suffix || '', placeholder: 'suffix',
      onChange: e => onChange(Object.assign({}, block, { suffix: e.target.value || undefined })),
    }),
    h(MiniBtn, { onClick: onRemove, danger: true, title: 'Remove line' }, '×')
  );
}

function ColumnMenu({ column, columns, audience, onPatch, onClose }) {
  const idx = columns.findIndex(c => c.id === column.id);
  const isImage = column.kind === 'image';
  const isCustom = column.kind === 'custom';

  function patchCol(patch) {
    onPatch(cols => cols.map(c => c.id === column.id ? Object.assign({}, c, patch) : c));
  }
  function patchBlock(bi, next) {
    const blocks = (column.blocks || []).slice();
    blocks[bi] = next;
    patchCol({ blocks });
  }
  function removeBlock(bi) {
    patchCol({ blocks: (column.blocks || []).filter((_, i) => i !== bi) });
  }

  return h('div', {
    style: {
      position: 'absolute', top: '100%', left: 0, zIndex: 60, width: 340,
      background: '#fff', border: '0.5px solid ' + LINE_CONTAINER, borderRadius: 6,
      boxShadow: '0 8px 32px rgba(0,0,0,0.14)', padding: 12,
    },
    onClick: e => e.stopPropagation(),
  },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } },
      h('div', { style: EYEBROW }, isImage ? 'Image column' : isCustom ? 'Custom column' : 'Column'),
      h('div', { style: { flex: 1 } }),
      h(MiniBtn, { onClick: () => onPatch(cols => moveColumn(cols, column.id, -1)), disabled: idx <= 0, title: 'Move left' }, '◀'),
      h(MiniBtn, { onClick: () => onPatch(cols => moveColumn(cols, column.id, 1)), disabled: idx >= columns.length - 1, title: 'Move right' }, '▶'),
      h(MiniBtn, {
        onClick: () => { onPatch(cols => removeColumn(cols, column.id)); onClose(); },
        danger: true, title: 'Remove column',
      }, 'Remove'),
      h(MiniBtn, { onClick: onClose }, 'Done')
    ),
    !isImage && h('div', { style: { display: 'flex', gap: 8, marginBottom: 10 } },
      h('div', { style: { flex: 1 } },
        h('div', { style: Object.assign({}, EYEBROW, { marginBottom: 3 }) }, 'Header label'),
        h('input', {
          style: Object.assign({}, INPUT, { width: '100%' }), value: column.label || '',
          onChange: e => patchCol({ label: e.target.value }),
        })
      ),
      h('div', null,
        h('div', { style: Object.assign({}, EYEBROW, { marginBottom: 3 }) }, 'Width'),
        h('input', {
          type: 'number', min: MIN_UNITS, style: Object.assign({}, INPUT, { width: 64 }),
          value: column.width || 20,
          onChange: e => onPatch(cols => resizeColumn(cols, column.id, parseFloat(e.target.value) || MIN_UNITS)),
        })
      )
    ),
    !isImage && !isCustom && h('div', null,
      h('div', { style: Object.assign({}, EYEBROW, { marginBottom: 5 }) }, 'Lines in this column'),
      (column.blocks || []).map((b, bi) =>
        h(BlockRow, { key: bi, block: b, audience, onChange: next => patchBlock(bi, next), onRemove: () => removeBlock(bi) })
      ),
      h('div', { style: { display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' } },
        h(MiniBtn, { onClick: () => patchCol({ blocks: (column.blocks || []).concat([{ kind: 'field', path: 'notes' }]) }) }, '+ Field line'),
        h(MiniBtn, { onClick: () => patchCol({ blocks: (column.blocks || []).concat([{ kind: 'text', value: '' }]) }) }, '+ Text line'),
        h('div', { style: { flex: 1 } }),
        h('span', { style: { fontSize: 10, color: INK_3 } }, 'Join:'),
        h('select', {
          style: Object.assign({}, INPUT, { width: 100 }),
          value: column.join == null ? '\n' : column.join,
          onChange: e => patchCol({ join: e.target.value === '\n' ? undefined : e.target.value }),
        },
          h('option', { value: '\n' }, 'Stacked lines'),
          h('option', { value: ' ' }, 'Inline (space)'),
          h('option', { value: ' · ' }, 'Inline ( · )')
        )
      )
    ),
    isCustom && h('div', { style: { fontSize: 11, color: INK_2 } },
      'A custom column holds free text typed per row on THIS schedule only — it never touches item records. Cell entry arrives with editing (Phase 4).')
  );
}

// ── The grid ─────────────────────────────────────────────────────────────────

export default function ScheduleGrid(props) {
  const { template, scheduleDoc, items, assemblies, canEditTemplates, onAutosaveTemplate, onForkTemplate, onEditStatus, onCellCommit } = props;
  // Phase 4 — inline cell editing. editing = { itemId, colId, field|null, kind, value }
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [menuColId, setMenuColId] = useState(null);
  const [forkOpen, setForkOpen] = useState(false);
  const [forkName, setForkName] = useState('');
  const [dragTick, setDragTick] = useState(0); // bumped on resize mouseup so autosave fires post-drag
  const dragRef = useRef(null);
  const draftRef = useRef(null);

  // Local edits reset when the active template changes identity (picker/fork).
  useEffect(() => { setDraft(null); draftRef.current = null; setMenuColId(null); }, [template && template.id]);

  const tpl = draft || template;
  draftRef.current = draft;

  // Autosave — templates are a live single source (Cimon, 2026-07-07):
  // structure edits persist as you go, billing-summary debounce pattern.
  // Mid-drag resizes wait for mouseup (dragTick); an edit that violates
  // the audience gate is held locally and never saved.
  useEffect(() => {
    const v = templateAudienceViolations(draft || template || {});
    if (onEditStatus) onEditStatus({ violations: v });
    if (!canEditTemplates || !draft) return undefined;
    if (dragRef.current) return undefined;
    if (v.length > 0) return undefined;
    const t = setTimeout(() => {
      if (draftRef.current && onAutosaveTemplate) onAutosaveTemplate(draftRef.current);
    }, 1200);
    return () => clearTimeout(t);
  }, [draft, dragTick, canEditTemplates]);

  const bags = useMemo(() => scheduleRowBagsForTemplate({
    todayIso: new Date().toISOString().slice(0, 10), // grid-only overdue tint
    template: tpl,
    selectedItems: (items || []).filter(it => (scheduleDoc.itemIds || []).indexOf(it.id) >= 0),
    assemblies,
    scheduleType: scheduleDoc.scheduleType,
    audience: scheduleDoc.audience,
    allItems: items,
    project: props.project || null,   // tax context for client_price_final (insurance)
    vendors: props.vendors || null,   // receiver names for whereabouts
  }), [tpl, items, assemblies, scheduleDoc.itemIds, scheduleDoc.scheduleType, scheduleDoc.audience, props.project, props.vendors]);

  // Event-scoped install run (2026-08-24): the grid shows the same EVENT
  // bands the PDF will print — one per selected install event, in date
  // order, Unscheduled last (per-run transient; empty filter = room bands).
  const eventFilter = (scheduleDoc.audience === 'install' && Array.isArray(props.installEventFilter))
    ? props.installEventFilter : [];
  const rooms = useMemo(() => eventFilter.length > 0
    ? groupScheduleRowsByInstallEvent(
        bags,
        (items || []).filter(it => (scheduleDoc.itemIds || []).indexOf(it.id) >= 0),
        props.project || null)
    : groupScheduleRowsByRoom(bags),
  [bags, eventFilter.join('\x1F'), items, scheduleDoc.itemIds, props.project]);

  if (!tpl) return null;
  const columns = tpl.columns || [];
  const totalUnits = columns.reduce((s, c) => s + (c.width || 10), 0) || 1;
  const tableW = Math.round(totalUnits * UNIT_PX);
  const violations = templateAudienceViolations(tpl);

  function patchColumns(fn) {
    setDraft(Object.assign({}, tpl, { columns: fn(columns.map(c => Object.assign({}, c))) }));
  }

  // Header-divider drag resize.
  function startResize(e, col) {
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { id: col.id, startX: e.clientX, startW: col.width || 10 };
    function onMove(ev) {
      const d = dragRef.current;
      if (!d) return;
      const units = d.startW + (ev.clientX - d.startX) / UNIT_PX;
      patchColumns(cols => resizeColumn(cols, d.id, units));
    }
    function onUp() {
      dragRef.current = null;
      setDragTick(t => t + 1);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  // What kind of edit does this cell support?
  //   'item'   — single registry field with a write target, item pre-lock or
  //              operational → writes THROUGH to the item (two-way), with a
  //              'this schedule only' escape hatch (override).
  //   'custom' — custom column → schedules.custom_values.
  //   'locked' — single-field cell whose write target is material and the
  //              item is committed/cancelled → read-only with lock tooltip.
  //   null     — derived/composite → not editable.
  function cellEditKind(col, bag) {
    if (!onCellCommit || !bag || !bag._itemId) return null;
    if (col.kind === 'image') return null;
    if (col.kind === 'custom') return 'custom';
    const fieldBlocks = (col.blocks || []).filter(b => b && b.kind === 'field' && b.path);
    const single = fieldBlocks.length > 0 ? fieldBlocks[0].path : '';
    if (!single) return null;
    const meta = SCHEDULE_FIELDS[single];
    if (!meta || !meta.itemField) return null;
    const item = (items || []).find(it => it.id === bag._itemId);
    if (!item) return null;
    return canEditScheduleField(single, item) ? 'item' : 'locked';
  }

  function startEdit(col, bag, kind) {
    const fb = (col.blocks || []).filter(b => b && b.kind === 'field' && b.path);
    const field = fb.length > 0 ? fb[0].path : null;
    const overrides = ((scheduleDoc.cellOverrides || {})[bag._itemId]) || {};
    const current = kind === 'custom'
      ? String((((scheduleDoc.customValues || {})[bag._itemId]) || {})[col.id] || '')
      : Object.prototype.hasOwnProperty.call(overrides, field)
        ? String(overrides[field])
        : String(bag[field] == null ? '' : bag[field]);
    setEditing({ itemId: bag._itemId, colId: col.id, field, kind, value: current });
  }

  function commitEdit(target) { // target: 'item' | 'override' | 'custom'
    if (!editing) return;
    onCellCommit({
      kind: target,
      itemId: editing.itemId,
      field: editing.field,
      colId: editing.colId,
      value: editing.value,
    });
    setEditing(null);
  }

  function cellEditor(col, bag, kind) {
    const hasOverride = (kind === 'item' || kind === 'locked') && editing.field &&
      Object.prototype.hasOwnProperty.call(((scheduleDoc.cellOverrides || {})[bag._itemId]) || {}, editing.field);
    return h('span', { onClick: e => e.stopPropagation() },
      h('input', {
        autoFocus: true,
        value: editing.value,
        onChange: e => setEditing(Object.assign({}, editing, { value: e.target.value })),
        onKeyDown: e => {
          if (e.key === 'Enter') commitEdit(kind === 'custom' ? 'custom' : kind === 'locked' ? 'override' : 'item');
          if (e.key === 'Escape') setEditing(null);
        },
        onBlur: e => {
          // Blur commits unless focus moved to one of the editor's own links.
          if (e.relatedTarget && e.relatedTarget.dataset && e.relatedTarget.dataset.celllink) return;
          commitEdit(kind === 'custom' ? 'custom' : kind === 'locked' ? 'override' : 'item');
        },
        style: {
          width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 12,
          color: INK_1, padding: '3px 6px', border: '1px solid ' + INK_1, borderRadius: 3, background: '#fff',
        },
      }),
      kind === 'locked' && h('span', { style: { display: 'block', fontSize: 10, color: INK_3, marginTop: 4 } },
        'Saves to THIS SCHEDULE ONLY \u2014 the item record keeps its value (Client Approved lock)'),
      (kind === 'item' || kind === 'locked') && h('span', { style: { display: 'flex', gap: 8, marginTop: 4 } },
        h('button', {
          'data-celllink': '1',
          onMouseDown: e => e.preventDefault(),
          onClick: () => commitEdit('override'),
          title: 'Show this value on this schedule only — the item record keeps its own value',
          style: { background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 10, color: INK_3, textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer' },
        }, 'this schedule only'),
        hasOverride && h('button', {
          'data-celllink': '1',
          onMouseDown: e => e.preventDefault(),
          onClick: () => { onCellCommit({ kind: 'clearOverride', itemId: editing.itemId, field: editing.field, colId: editing.colId }); setEditing(null); },
          style: { background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 10, color: INK_3, textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer' },
        }, 'clear override')
      )
    );
  }

  function cellContent(col, bag) {
    const resolved = resolveScheduleCell(col, bag, scheduleDoc);
    if (resolved && resolved.kind === 'image') {
      return resolved.url
        ? h('img', { src: resolved.url, style: { width: 36, height: 36, objectFit: 'cover', border: '1px solid ' + LINE_CONTAINER, borderRadius: 4, display: 'block' } })
        : null;
    }
    const s = String(resolved || '');
    if (!s) return h('span', { style: { color: INK_4 } }, '—');
    return s.split('\n').map((ln, i) => h('div', { key: i }, ln));
  }

  const colStyle = (c) => ({
    width: Math.round((c.width || 10) * UNIT_PX), minWidth: Math.round((c.width || 10) * UNIT_PX),
    maxWidth: Math.round((c.width || 10) * UNIT_PX),
    padding: '10px', borderRight: '1px solid ' + LINE_ROW,
    fontSize: 12, color: INK_2, verticalAlign: 'top',
    overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box',
  });

  return h('div', { onClick: () => setMenuColId(null) },
    // Grid hint (handoff §6.4) — the whisper/violation line renders in the
    // panel's action row via onEditStatus, not here.
    canEditTemplates && h('div', { style: { fontSize: 11, color: INK_3, margin: '0 0 8px' } },
      'Drag column edges to resize \u00b7 click a header to rename, move, re-map, or remove \u00b7 this grid is the template \u2014 what you shape here is what publishes'),
    // The grid scrolls INSIDE this container (both axes, viewport-capped
    // height) so the horizontal scrollbar is always reachable at the
    // container's bottom edge and the header row can stick (Cimon
    // 2026-07-29: scroll up and down and still know the column titles).
    h('div', { style: { overflow: 'auto', maxHeight: 'calc(100vh - 240px)', border: '1px solid ' + LINE_CONTAINER, borderRadius: 6 } },
      // borderCollapse 'separate': collapsed borders detach from sticky
      // headers on scroll; every cell already paints its own edges.
      h('table', { style: { borderCollapse: 'separate', borderSpacing: 0, width: tableW, tableLayout: 'fixed' } },
        h('thead', null,
          h('tr', null,
            columns.map((c, ci) =>
              h('th', {
                key: c.id || ci,
                style: Object.assign({}, colStyle(c), {
                  position: 'sticky', top: 0, zIndex: 4,
                  textAlign: 'left', cursor: canEditTemplates ? 'pointer' : 'default',
                  padding: '8px 10px', borderBottom: '1px solid #111111', background: '#fff',
                  fontSize: 10, letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: menuColId === c.id ? ZONE_FUCHSIA : INK_3, fontWeight: 500,
                }),
                onClick: canEditTemplates ? (e => { e.stopPropagation(); setMenuColId(menuColId === c.id ? null : c.id); }) : undefined,
              },
                c.kind === 'image' ? (c.label || 'IMAGE') : (c.label || ' '),
                c.kind === 'custom' && h('span', { style: { color: INK_4, marginLeft: 4, textTransform: 'none', letterSpacing: 0 } }, '(schedule-only)'),
                canEditTemplates && h('div', {
                  onMouseDown: e => startResize(e, c),
                  onClick: e => e.stopPropagation(),
                  title: 'Drag to resize',
                  style: { position: 'absolute', top: 0, right: -3, width: 7, height: '100%', cursor: 'col-resize', zIndex: 5 },
                }),
                canEditTemplates && menuColId === c.id && h(ColumnMenu, {
                  column: c, columns, audience: tpl.audience,
                  onPatch: patchColumns, onClose: () => setMenuColId(null),
                })
              )
            )
          )
        ),
        h('tbody', null,
          rooms.length === 0
            ? h('tr', null, h('td', { colSpan: columns.length, style: { padding: 18, fontSize: 11, color: INK_4, textAlign: 'center' } }, 'No items selected.'))
            : rooms.map(room => {
                const bandLabel = [room.roomNumber, (room.room || '').toUpperCase()].filter(Boolean).join(' — ');
                // page.roomBands === false (Contractor/GC, 2026-07-23): no
                // per-room band rows — room order still sequences the rows.
                const bandRows = (tpl.page && tpl.page.roomBands === false) ? [] : [
                  h('tr', { key: 'band-' + room.roomNumber + room.room },
                    h('td', {
                      colSpan: columns.length,
                      style: {
                        padding: '6px 10px', fontSize: 10, fontWeight: 500, color: INK_2,
                        letterSpacing: '0.07em', textTransform: 'uppercase', background: GROUND_OFF,
                        borderBottom: '1px solid ' + LINE_ROW,
                      },
                    }, bandLabel)
                  ),
                ];
                return bandRows.concat(room.rows.map((bag, ri) => {
                  const pre = [];
                  if (bag._isFirstInAsm && bag._assemblyName) {
                    pre.push(h('tr', { key: 'asm-' + bag._itemId },
                      h('td', {
                        colSpan: columns.length,
                        style: { padding: '4px 10px', fontSize: 10, fontWeight: 500, color: INK_2, letterSpacing: '0.07em', textTransform: 'uppercase', borderBottom: '1px solid ' + LINE_ROW },
                      }, String(bag._assemblyName).toUpperCase() + (bag.tag ? '  ·  ' + bag.tag : ''))
                    ));
                  }
                  pre.push(h('tr', { key: bag._itemId + '-' + ri },
                    columns.map((c, ci) => {
                      const _fb = (c.blocks || []).filter(b => b && b.kind === 'field' && b.path);
                      const single = _fb.length > 0 ? _fb[0].path : '';
                      const editKind = cellEditKind(c, bag);
                      const isEditing = editing && editing.itemId === bag._itemId && editing.colId === c.id;
                      const overridesForItem = ((scheduleDoc.cellOverrides || {})[bag._itemId]) || {};
                      const hasOverride = single && Object.prototype.hasOwnProperty.call(overridesForItem, single);
                      const baseVal = single ? String(bag[single] == null ? '' : bag[single]) : '';
                      return h('td', {
                        key: c.id || ci,
                        'data-item-id': bag._itemId || '',
                        'data-field': single,
                        'data-editable': editKind === 'item' || editKind === 'custom' ? '1' : '',
                        'data-locked': editKind === 'locked' ? '1' : '',
                        title: editKind === 'locked'
                          ? 'Locked at Client Approved — item edits go through a Change Order. Click to show a different value on THIS SCHEDULE ONLY (the item record is untouched).'
                          : hasOverride
                            ? 'Override on this schedule only — the item record says: ' + (baseVal || '(blank)')
                            : (editKind ? 'Click to edit' : ''),
                        onClick: editKind
                          ? (e => { e.stopPropagation(); if (!isEditing) startEdit(c, bag, editKind); })
                          : undefined,
                        style: Object.assign({}, colStyle(c), {
                          borderBottom: '1px solid ' + LINE_ROW,
                          whiteSpace: 'pre-line', // honor hard breaks (PLANNED INSTALL label/date)
                          color: (single === 'install_planned' && bag._install_overdue) ? '#665518' : ci === 1 ? INK_1 : INK_2,
                          fontWeight: ci === 1 ? 500 : 400,
                          cursor: editKind ? 'text' : 'default',
                        }),
                      },
                        isEditing
                          ? cellEditor(c, bag, editing.kind)
                          : h('span', { style: { display: 'flex', alignItems: 'flex-start', gap: 6 } },
                              hasOverride && h('span', {
                                'data-testid': 'override-dot',
                                style: { width: 6, height: 6, borderRadius: '50%', background: ZONE_FUCHSIA, flexShrink: 0, marginTop: 5 },
                              }),
                              h('span', { style: { minWidth: 0 } }, cellContent(c, bag))
                            )
                      );
                    })
                  ));
                  return pre;
                }));
              })
        )
      )
    ),
    canEditTemplates && h('div', { style: { display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }, onClick: e => e.stopPropagation() },
      h('button', {
        style: { fontFamily: 'inherit', fontSize: 12, fontWeight: 500, padding: '7px 18px', borderRadius: 6, border: '1px solid ' + LINE_CONTAINER, background: '#fff', color: INK_1, cursor: 'pointer' },
        onClick: () => patchColumns(cols => cols.concat([newFieldColumn(listScheduleFieldsForAudience(tpl.audience).filter(k => k !== 'image')[0] || 'notes')])),
      }, '+ Add field column'),
      h('button', {
        style: { fontFamily: 'inherit', fontSize: 12, fontWeight: 500, padding: '7px 18px', borderRadius: 6, border: '1px solid ' + LINE_CONTAINER, background: '#fff', color: INK_1, cursor: 'pointer' },
        onClick: () => patchColumns(cols => cols.concat([newCustomColumn()])),
      }, '+ Add custom column'),
      h('div', { style: { flex: 1 } }),
      forkOpen
        ? h('span', { style: { display: 'flex', gap: 6, alignItems: 'center' } },
            h('input', {
              style: Object.assign({}, INPUT, { width: 230 }),
              placeholder: (template.name || 'Template') + ' — variant',
              value: forkName, autoFocus: true,
              onChange: e => setForkName(e.target.value),
            }),
            h(MiniBtn, {
              onClick: () => {
                if (onForkTemplate) onForkTemplate(forkName || ((template.name || 'Template') + ' — variant'), draft || tpl);
                setForkOpen(false); setForkName('');
              },
            }, 'Create'),
            h(MiniBtn, { onClick: () => { setForkOpen(false); setForkName(''); } }, 'Cancel'))
        : h('button', {
            onClick: () => setForkOpen(true),
            title: 'Keep this layout as a separate named template — e.g. a variant to reuse on another project without changing this one',
            style: { background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 12, color: INK_2, textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer' },
          }, 'New template from this layout…')
    )
  );
}
